<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3';
import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import InputError from '@/Components/InputError.vue';
import Message from 'primevue/message';

defineProps({
    canResetPassword: Boolean,
    status: String,
});

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    });
};
</script>

<template>
    <Head title="Log in" />
    <AuthenticationCard>
        <template #header>
            <div class="flex flex-col items-center justify-center space-y-2 p-8">
                <span class="text-sm font-bold">Agency Account</span><span class="text-3xl font-bold">Sign In</span>
            </div>
        </template>

        <Message v-if="$page.props.flash.status" class="mb-6" severity="success" closable>
            {{ $page.props.flash.status }}
        </Message>
        <Message v-if="$page.props.flash.error" class="mb-6" severity="error" closable>
            {{ $page.props.flash.error }}
        </Message>
        <form class="flex flex-col gap-y-4" @submit.prevent="submit">
            <div>
                <InputText
                    id="email"
                    v-model="form.email"
                    :invalid="!!form.errors.email"
                    fluid
                    placeholder="Your email"
                    :class="{ 'border-gray-200 bg-gray-200': !form.errors.email }"
                    class="dark:text-primary-contrast"
                />
                <InputError class="mt-2 pl-4" :message="form.errors.email" />
            </div>
            <div>
                <Password
                    id="password"
                    v-model="form.password"
                    :invalid="!!form.errors.password"
                    toggle-mask
                    :feedback="false"
                    fluid
                    placeholder="Your password"
                    rounded
                    class="dark:text-primary-contrast"
                    :pt="{
                        pcinputtext: {
                            root: {
                                class: `dark:text-primary-contrast ${!form.errors.password && 'bg-gray-200 border-gray-200'}`,
                            },
                        },
                    }"
                />
                <InputError class="mt-2 pl-4" :message="form.errors.password" />
            </div>
            <div class="px-4">
                <div class="flex items-center">
                    <Checkbox v-model="form.remember" input-id="remember" :binary="true" />
                    <label for="remember" class="ml-2"> Remember me </label>
                </div>
            </div>
            <div class="mt-4 flex flex-col-reverse items-center justify-end gap-4 sm:flex-row">
                <Link
                    v-if="canResetPassword"
                    :href="route('password.request')"
                    class="rounded-md text-sm text-gray-600 underline hover:text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:text-primary"
                >
                    Forgot your password?
                </Link>
                <Button
                    class="ms-4 bg-primary-500 px-8 font-bold dark:bg-primary-200"
                    :class="{ 'opacity-50': form.processing }"
                    type="submit"
                    :loading="form.processing"
                    size="large"
                    rounded
                >
                    Sign in
                </Button>
            </div>
        </form>
    </AuthenticationCard>
</template>
